import { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import { useTranslation } from "react-i18next";
import {
  isMykokuFormsUrl,
  openMykokuFormWithAutofill,
} from "../../helpers/formsAutofill";
import { copyQrValue, getOpenableQrUrl } from "../../helpers/qrContent";

// A self-contained full-screen QR scanner. MYKOKU Forms links retain their
// Auto-fill Pass flow; every other decoded value is shown for safe review,
// copying, and optional opening when its URI scheme is supported.
//
// Forms links are handed to the phone's default browser (window.open) rather
// than opened in place. This app is display:standalone, so navigating in place
// keeps the form inside the installed shell with no address bar, leaving the
// visitor no way to reach their active pass again afterwards.
//
// That is best-effort, not guaranteed: a camera decode callback is not a
// direct user gesture, so the browser may refuse the new window. When it does
// we fall back to a same-tab navigation, which is what this always did before
// — never worse than before, and better whenever the browser allows it.

type ScanStatus =
  | "starting"
  | "scanning"
  | "processing"
  | "result"
  | "camera-error";

type FormsQrScannerModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const FormsQrScannerModal = ({ isOpen, onClose }: FormsQrScannerModalProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const isHandlingRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const [status, setStatus] = useState<ScanStatus>("starting");
  const [resultValue, setResultValue] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const stopVideoTracks = (
    video: HTMLVideoElement | null = videoRef.current,
  ) => {
    const stream = video?.srcObject;
    if (video && stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  };

  const stopAndDestroy = async () => {
    const scanner = scannerRef.current;
    const video = scanner?.$video ?? videoRef.current;
    scannerRef.current = null;
    if (scanner) {
      try {
        // `stop()` waits before releasing the MediaStream. `pause(true)`
        // releases it immediately, including while start() is still pending.
        await scanner.pause(true);
      } catch {
        // Fall through to the direct MediaStream cleanup below.
      }
      scanner.destroy();
    }
    stopVideoTracks(video);
  };

  const closeScanner = async () => {
    isOpenRef.current = false;
    await stopAndDestroy();
    onClose();
  };

  const showGenericResult = async (value: string) => {
    isHandlingRef.current = true;
    setResultValue(value);
    setCopied(false);
    setStatus("result");
    try {
      await scannerRef.current?.pause(true);
    } catch {
      stopVideoTracks();
    }
  };

  const scanAgain = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    isHandlingRef.current = false;
    setResultValue(null);
    setCopied(false);
    setStatus("starting");
    try {
      await scanner.start();
      if (isOpenRef.current) setStatus("scanning");
    } catch {
      if (isOpenRef.current) setStatus("camera-error");
    }
  };

  const copyResult = async () => {
    if (!resultValue) return;
    setCopied(await copyQrValue(resultValue));
  };

  const openResult = () => {
    if (!resultValue) return;
    const target = getOpenableQrUrl(resultValue);
    if (!target) return;
    const opened = window.open(target, "_blank", "noopener,noreferrer");
    if (!opened) window.location.assign(target);
    void closeScanner();
  };

  const handleDecoded = async (rawValue: string) => {
    const value = rawValue.trim();
    if (!value || isHandlingRef.current) return;

    if (!isMykokuFormsUrl(value)) {
      await showGenericResult(value);
      return;
    }

    isHandlingRef.current = true;
    setStatus("processing");

    // Stop scanning immediately so no further frames fire while minting.
    try {
      await scannerRef.current?.pause(true);
    } catch {
      stopVideoTracks();
    }

    await openMykokuFormWithAutofill(value, (targetUrl) => {
      // Same strategy as openResult() below: prefer the phone's default
      // browser, fall back to this tab when the popup is refused.
      const opened = window.open(targetUrl, "_blank", "noopener,noreferrer");
      if (!opened) window.location.assign(targetUrl);
    });

    // The navigation above unloads this page on success; if the user closed
    // the modal mid-mint instead, don't touch state after the fact.
    if (isOpenRef.current) await closeScanner();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    isHandlingRef.current = false;
    setStatus("starting");
    setResultValue(null);
    setCopied(false);
    let cancelled = false;

    const start = async () => {
      let scanner: QrScanner | null = null;
      try {
        const hasCamera = await QrScanner.hasCamera();
        if (cancelled) return;
        if (!hasCamera) {
          setStatus("camera-error");
          return;
        }
        if (!videoRef.current) return;

        scanner = new QrScanner(
          videoRef.current,
          (result) => {
            void handleDecoded(result.data);
          },
          {
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 1,
          },
        );
        scannerRef.current = scanner;
        await scanner.start();
        if (cancelled) {
          if (scannerRef.current === scanner) await stopAndDestroy();
          return;
        }
        setStatus("scanning");
      } catch {
        if (scanner && scannerRef.current === scanner) {
          await stopAndDestroy();
        }
        if (!cancelled) setStatus("camera-error");
      }
    };

    start();

    return () => {
      cancelled = true;
      void stopAndDestroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-lg font-semibold">{t("qrScanner.title")}</span>
        <button
          type="button"
          aria-label={t("qrScanner.close")}
          className="rounded-full bg-white/10 px-3 py-1 text-xl leading-none"
          onClick={() => void closeScanner()}
        >
          ×
        </button>
      </div>

      <div className="relative flex flex-1 flex-col items-center justify-center px-4">
        {status !== "camera-error" && status !== "result" && (
          <video
            ref={videoRef}
            className="w-full max-w-sm rounded-2xl bg-black"
            muted
            playsInline
          />
        )}

        {status === "processing" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="text-white">{t("qrScanner.opening")}</span>
          </div>
        )}

        {status === "camera-error" && (
          <p className="max-w-xs text-center text-sm text-white/90">
            {t("qrScanner.cameraError")}
          </p>
        )}

        {status === "result" && resultValue && (
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 text-gray-900 shadow-xl">
            <h2 className="text-lg font-semibold">{t("qrScanner.resultTitle")}</h2>
            <p className="mt-1 text-sm text-gray-500">{t("qrScanner.resultHint")}</p>
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("qrScanner.contentLabel")}
            </p>
            <p className="mt-2 max-h-40 overflow-auto break-all rounded-xl bg-gray-100 p-3 text-sm">
              {resultValue}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {getOpenableQrUrl(resultValue) && (
                <button
                  type="button"
                  className="rounded-xl bg-blue-600 px-4 py-3 font-medium text-white"
                  onClick={openResult}
                >
                  {t("qrScanner.open")}
                </button>
              )}
              <button
                type="button"
                className={`${getOpenableQrUrl(resultValue) ? "" : "col-span-2 "}rounded-xl bg-gray-200 px-4 py-3 font-medium text-gray-900`}
                onClick={() => void copyResult()}
              >
                {copied ? t("qrScanner.copied") : t("qrScanner.copy")}
              </button>
              <button
                type="button"
                className="col-span-2 rounded-xl border border-gray-300 px-4 py-3 font-medium"
                onClick={() => void scanAgain()}
              >
                {t("qrScanner.scanAgain")}
              </button>
              <button
                type="button"
                className="col-span-2 px-4 py-2 font-medium text-gray-600"
                onClick={() => void closeScanner()}
              >
                {t("qrScanner.close")}
              </button>
            </div>
          </div>
        )}

        <p className="mt-4 min-h-[1.5rem] text-center text-sm text-white/90">
          {status === "starting" && t("qrScanner.starting")}
          {status === "scanning" && t("qrScanner.hint")}
        </p>
      </div>
    </div>
  );
};

export default FormsQrScannerModal;
